import React from 'react';
import styled from 'styled-components';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { hexToRGBA } from '../../utils/hexToRGBA';
import { STEP_STATUS, resolveStepStatus } from './stepStatus';

const MARKER_SIZE = '36px';
const MARKER_ICON_SIZE = 20;
const CONNECTOR_THICKNESS = '2px';
const CONNECTOR_OFFSET = '17px';
const ACTIVE_RING_WIDTH = '5px';
const UPCOMING_COLOR = '#C9CFD8';
const UPCOMING_TEXT_COLOR = '#6B7684';
const ERROR_COLOR = '#D32F2F';
const LABEL_COLOR = '#10253E';
const NARROW_SCREEN = '480px';

const isFilled = (status) => status !== STEP_STATUS.upcoming;

const resolveMarkerColor = ({ $status, $themeColor }) => {
  if ($status === STEP_STATUS.error) return ERROR_COLOR;
  if ($status === STEP_STATUS.upcoming) return UPCOMING_COLOR;
  return $themeColor;
};

const StepList = styled.ol`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const StepEntryContainer = styled.li`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const Connector = styled.span`
  position: absolute;
  top: ${CONNECTOR_OFFSET};
  left: 50%;
  width: 100%;
  height: ${CONNECTOR_THICKNESS};
  background-color: ${({ $isPassed, $themeColor }) => ($isPassed ? $themeColor : UPCOMING_COLOR)};
  transition: background-color 250ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Marker = styled.span`
  z-index: 1;
  display: grid;
  place-items: center;
  width: ${MARKER_SIZE};
  height: ${MARKER_SIZE};
  border: ${CONNECTOR_THICKNESS} solid ${resolveMarkerColor};
  border-radius: 50%;
  background-color: ${(props) => (isFilled(props.$status) ? resolveMarkerColor(props) : '#ffffff')};
  color: ${({ $status }) => (isFilled($status) ? '#ffffff' : UPCOMING_TEXT_COLOR)};
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  box-shadow: ${({ $status, $themeColor }) =>
    $status === STEP_STATUS.active ? `0 0 0 ${ACTIVE_RING_WIDTH} ${hexToRGBA($themeColor, 0.2)}` : 'none'};
  transition: background-color 250ms ease, border-color 250ms ease, box-shadow 250ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StepLabel = styled.span`
  max-width: 120px;
  color: ${({ $status }) => ($status === STEP_STATUS.upcoming ? UPCOMING_TEXT_COLOR : LABEL_COLOR)};
  font-size: 14px;
  font-weight: ${({ $status }) => ($status === STEP_STATUS.upcoming ? 400 : 600)};
  line-height: 1.3;
  text-align: center;
  text-wrap: pretty;

  @media (max-width: ${NARROW_SCREEN}) {
    display: ${({ $status }) => ($status === STEP_STATUS.upcoming ? 'none' : 'block')};
  }
`;

const ScreenReaderText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

const MarkerContent = ({ status, position }) => {
  if (status === STEP_STATUS.complete) return <CheckIcon sx={{ fontSize: MARKER_ICON_SIZE }} />;
  if (status === STEP_STATUS.error) return <PriorityHighIcon sx={{ fontSize: MARKER_ICON_SIZE }} />;
  return position;
};

const StepEntry = ({ step, status, position, connector, themeColor }) => (
  <StepEntryContainer aria-current={status === STEP_STATUS.active ? 'step' : undefined}>
    {connector.isVisible && <Connector $isPassed={connector.isPassed} $themeColor={themeColor} />}
    <Marker $status={status} $themeColor={themeColor} aria-hidden="true">
      <MarkerContent status={status} position={position} />
    </Marker>
    <StepLabel $status={status}>
      <ScreenReaderText>{`Step ${position}: `}</ScreenReaderText>
      {step.label}
    </StepLabel>
  </StepEntryContainer>
);

export const ProgressStepper = ({ steps, activeIndex, hasError = false, $themeColor = LABEL_COLOR }) => (
  <StepList aria-label="Connection progress">
    {steps.map((step, index) => (
      <StepEntry
        key={step.id}
        step={step}
        position={index + 1}
        themeColor={$themeColor}
        status={resolveStepStatus({ index, activeIndex, hasError })}
        connector={{ isVisible: index < steps.length - 1, isPassed: index < activeIndex }}
      />
    ))}
  </StepList>
);
